import type { Doc } from "building-types/doc";
import { camelCaseToTitleCase } from "common/utils/strings";
import { docEntityTableFieldsToEntityType } from "doc/entity/docEntityTableFieldsToEntityType";

export const docFieldAffectsBuilding: { readonly [key in keyof Doc]: boolean } = {
  annotation: true,
  attributes: false,
  blocks: true,
  casework: true,
  ceilings: true,
  components: true,
  componentInstances: true,
  componentMembers: true,
  columns: true,
  docRefs: false,
  dormers: true,
  drawingRefs: true,
  edgeModifiers: true,
  electricalDevices: true,
  electricalLayouts: true,
  elevations: true,
  elevationViews: false,
  entityGroups: false,
  entityRefs: false,
  entityRoomLayouts: true,
  facades: true,
  facadeSpanStrata: true,
  facadeSweepStrata: true,
  floors: true,
  floorFinishes: true,
  foundations: true,
  foundationSystems: true,
  roofOpenings: true,
  gridSnapDistance: false,
  groups: true,
  hidden: false,
  isParsed: false,
  levels: true,
  compositeLevels: true,
  locked: false,
  modifiers: true,
  measurements: true,
  name: false,
  openings: true,
  optionChoices: true,
  componentAssociations: true,
  optionSetOverrides: true,
  optionSets: true,
  instanceOptionSets: true,
  optionTemplates: true,
  optionEffects: true,
  optionRules: true,
  optionLocks: true,
  palettes: true,
  planViews: false,
  railingPosts: true,
  presets: false,
  profiles: true,
  propAttributes: true,
  propInstances: true,
  railingCatalog: true,
  railings: true,
  sites: true,
  siteShapes: true,
  referenceGeom: true,
  roofs: true,
  roofSystems: true,
  roofVolumes: true,
  floorSystems: true,
  edgeBehaviors: true,
  rooms: true,
  roomLayouts: true,
  roomLayoutInstances: true,
  schemaVersion: false,
  settings: true,
  settingsSets: true,
  entitySettings: true,
  sheets: true,
  skylights: true,
  snapEntities: false,
  snapKinds: false,
  stairBlocks: true,
  stairSystems: true,
  stairCatalog: true,
  underlays: false,
  roofUnderlays: false,
  assets: true,
  view3Ds: false,
  planSetTemplates: false,
  viewOverrides: true,
  selectors: true,
  viewSelectors: true,
  planSetTemplateConfigs: false,
  contentTemplates: true,
  contentTemplateInstances: true,
  contentReplicators: false,
  localParameters: false,
  globalParameters: false,
  viewports: false,
  viewTemplates: false,
  viewTemplateOverrides: false,
  dataTables: false,
  dataSequences: false,
  sectionViews: false,
  labels: false,
  effects: false,
  datums: true,
  ruleBasedSelectors: false,
  ruleBasedSelectorInstances: false,
  facadeSpanStratumAssociations: true,
  facadeSweepStratumAssociations: true,
  datumSpanAssociations: true,
  headerAssemblies: true,
  framingComponents: true,
  masonryComponents: true,
  cipComponents: true,
  sheathingComponents: true,
  foundationAssemblies: true,
  foundationComponents: true,
  frameFloorAssemblies: true,
  slabFloorAssemblies: true,
  slabComponents: true,
  wallPlaneAssemblies: true,
  wallPlaneFilters: true,
  wallPlaneRules: true,
  wallPlaneTrims: true,
  freeformWalls: true,
  reportableUnitDesignations: false,
} as const;

export function selectFieldsThatAffectBuilding(doc: Doc): Partial<Doc> {
  const subDoc: Partial<Doc> = {};

  for (const fieldId in docFieldAffectsBuilding) {
    if (docFieldAffectsBuilding[fieldId]) {
      subDoc[fieldId] = doc[fieldId];
    }
  }

  return subDoc;
}

export function docFieldToExplorerTitle(field: keyof Doc): string | undefined {
  if (!docEntityTableFieldsToEntityType[field]) {
    return undefined;
  }

  switch (field) {
    case "referenceGeom":
      return "Reference Geometry";
    case "view3Ds":
      return "3D Views";
    case "planViews":
      return "Plan Views";
    case "labels":
      return "Labels";
    default:
      return camelCaseToTitleCase(field);
  }
}

const keysOfDocFieldAffectsBuilding = Object.keys(docFieldAffectsBuilding).filter(
  (fieldId) => docFieldAffectsBuilding[fieldId],
);

export function docGetFieldsThatAffectBuilding(doc: Doc | undefined) {
  return keysOfDocFieldAffectsBuilding.map((fieldId) => doc?.[fieldId]);
}

const keysOfDocGetFieldsThatDoNotAffectBuilding = Object.keys(docFieldAffectsBuilding).filter(
  (fieldId) => !docFieldAffectsBuilding[fieldId],
);

export function docGetFieldsThatDoNotAffectBuilding(doc: Doc) {
  return keysOfDocGetFieldsThatDoNotAffectBuilding.map((fieldId) => doc?.[fieldId]);
}
